import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { adminTunesService, TuneWithAnalysis } from '@/services/adminTunesService';
import { toastHelper } from '@/utils/toastHelper';
import { RefreshCw, Music, Eye } from 'lucide-react';

interface UserDetailTunesProps {
  userId: string;
}

const UserDetailTunes: React.FC<UserDetailTunesProps> = ({ userId }) => {
  const [tunes, setTunes] = useState<TuneWithAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 25;

  const fetchTunes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminTunesService.searchTunes({
        user_id: userId,
        limit: pageSize,
        offset: currentPage * pageSize
      });
      setTunes(data.tunes);
      setTotalCount(data.total);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch tunes');
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage]);

  useEffect(() => {
    fetchTunes();
  }, [fetchTunes]);

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      processing: 'secondary',
      pending: 'outline',
      failed: 'destructive'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4">
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading tunes...</p>
          </CardContent>
        </Card>
      ) : tunes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Music className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No tunes found for this user</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">User Tunes ({totalCount} total)</h3>
                <p className="text-sm text-muted-foreground">
                  Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchTunes}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {tunes.map((tune) => (
              <Card key={tune.id}>
                <CardContent className="p-4 space-y-3">
                  {/* Title and Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm line-clamp-2">{tune.video_title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">ID: {tune.id}</p>
                    </div>
                    {tune.audio_analysis
                      ? getStatusBadge(tune.audio_analysis.extraction_status)
                      : <Badge variant="outline" className="text-xs flex-shrink-0">No Analysis</Badge>}
                  </div>

                  {/* YouTube ID and Upload Date */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">YouTube ID</p>
                      <p className="font-mono text-xs truncate">{tune.youtube_id || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Upload Date</p>
                      <p className="text-xs">{formatDate(tune.upload_date)}</p>
                    </div>
                  </div>

                  {/* Audio Info */}
                  {tune.audio_analysis && (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">BPM</p>
                        <p className="font-medium">
                          {tune.audio_analysis.detected_bpm
                            ? `${Math.round(tune.audio_analysis.detected_bpm)} BPM`
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Duration</p>
                        <p className="font-medium">{formatDuration(tune.audio_analysis.duration)}</p>
                      </div>
                    </div>
                  )}

                  {/* Privacy and Category */}
                  <div className="flex gap-2 flex-wrap pt-2 border-t">
                    <Badge variant="outline" className="text-xs">{tune.privacy_status}</Badge>
                    <Badge variant="secondary" className="text-xs">{tune.category}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={(currentPage + 1) * pageSize >= totalCount}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <Card className="hidden lg:block">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>User Tunes ({totalCount} total)</CardTitle>
                  <CardDescription>
                    Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchTunes}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>YouTube ID</TableHead>
                      <TableHead>Upload Date</TableHead>
                      <TableHead>BPM</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Privacy</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tunes.map((tune) => (
                      <TableRow key={tune.id}>
                        <TableCell>{tune.id}</TableCell>
                        <TableCell className="max-w-xs truncate">{tune.video_title}</TableCell>
                        <TableCell className="font-mono text-xs">{tune.youtube_id || 'N/A'}</TableCell>
                        <TableCell className="text-sm">{formatDate(tune.upload_date)}</TableCell>
                        <TableCell>
                          {tune.audio_analysis?.detected_bpm
                            ? `${Math.round(tune.audio_analysis.detected_bpm)} BPM`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{formatDuration(tune.audio_analysis?.duration || null)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{tune.privacy_status}</Badge>
                        </TableCell>
                        <TableCell>
                          {tune.audio_analysis
                            ? getStatusBadge(tune.audio_analysis.extraction_status)
                            : <Badge variant="outline">No Analysis</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Desktop Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={(currentPage + 1) * pageSize >= totalCount}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default UserDetailTunes;
